<template>
  <main class="schedule-page">
    <header class="schedule-topbar">
      <button class="brand" type="button" aria-label="Dashboard" @click="$router.push('/Dashboard')"><img src="/zaturdayicon.png" alt="" /></button>
      <div><h1>View Schedule</h1><p>อัปเดตล่าสุด: {{ formatClock(updatedAt) }}</p></div>
      <div class="account-actions">
        <button class="language" type="button" @click="toggleLanguage">{{ $i18n.locale.toUpperCase() }}</button>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <button class="profile" type="button" v-bind="attrs" v-on="on"><img src="@/assets/account-circle.png" alt="" /><span><strong>{{ username }}</strong><small>{{ username }}@Forward...</small></span><v-icon color="#00378e">mdi-menu-down</v-icon></button>
          </template>
          <v-list dense>
            <v-list-item @click="Logout">
              <v-list-item-title>Logout</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </header>

    <section class="booking-shell">
      <header class="booking-title"><span>Booking Workspace</span><button type="button" aria-label="Close" @click="$router.push('/Dashboard')"><v-icon color="white">mdi-close</v-icon></button></header>

      <div v-if="errorMessage" class="schedule-error" role="alert"><span>{{ errorMessage }}</span><button type="button" @click="loadPage">Retry</button></div>
      <div class="booking-layout">
        <aside class="facility-panel">
          <h2>Facility</h2>
          <label>Zone</label>
          <v-select v-model="selectedZoneID" :items="zoneOptions" item-text="text" item-value="value" dense outlined hide-details aria-label="Zone" @change="onZoneChange" />
          <v-select v-model="selectedFloorID" :items="floorOptions" item-text="text" item-value="value" dense outlined hide-details aria-label="Floor" class="floor-input" @change="onFloorChange" />
          <div class="room-heading"><h3>All Rooms</h3><small>({{ filteredRooms.length }} Room)</small></div>
          <button v-for="room in filteredRooms" :key="room.facilityID" type="button" :class="['room-option', { active: room.facilityID === selectedFacilityID }]" @click="selectRoom(room)">
            <i></i><span><strong>{{ room.name }}</strong><small><v-icon size="12" color="#00378e">mdi-account-outline</v-icon>10</small></span>
          </button>
          <div v-if="filteredRooms.length === 0" class="empty-copy">No rooms</div>
        </aside>

        <section class="timeline-panel">
          <div class="timeline-heading">
            <h2>Timeline {{ selectedRoom ? selectedRoom.name : '' }}</h2>
            <div class="date-nav"><button type="button" :aria-label="viewMode === 'D' ? 'Previous day' : 'Previous month'" @click="movePeriod(-1)"><v-icon color="#00378e">mdi-chevron-left</v-icon></button><strong>{{ viewMode === "D" ? formatDate(selectedDate) : formatMonth(selectedDate) }}</strong><button type="button" :aria-label="viewMode === 'D' ? 'Next day' : 'Next month'" @click="movePeriod(1)"><v-icon color="#00378e">mdi-chevron-right</v-icon></button></div>
            <div class="view-toggle"><button type="button" :class="{ active: viewMode === 'D' }" @click="setViewMode('D')">D</button><button type="button" :class="{ active: viewMode === 'M' }" @click="setViewMode('M')">M</button></div>
          </div>
          <div v-if="viewMode === 'D'" class="timeline-scroll">
            <div class="timeline-grid">
              <div v-for="hour in hours" :key="hour" class="hour-row">
                <time>{{ hourLabel(hour) }}</time>
                <div class="hour-slot">
                  <button v-for="booking in bookingsByHour[hour]" :key="booking.id" type="button" :aria-label="`${booking.bookingName}, ${formatTimeRange(booking)}`" :class="['booking-block', { compact: isCompactBooking(booking), selected: selectedBooking && selectedBooking.id === booking.id }]" @click="selectBooking(booking)">
                    <strong>{{ booking.bookingName }}</strong><small>{{ formatTimeRange(booking) }}</small><span class="booking-host"><img src="@/assets/account-circle.png" alt="" /><span class="sr-only">{{ booking.hostName }}</span></span>
                  </button>
                </div>
              </div>
              <div v-if="visibleBookings.length === 0 && !loading" class="timeline-empty">No meeting</div>
            </div>
          </div>
          <div v-else class="month-calendar">
            <div v-for="dayName in weekDays" :key="dayName" class="weekday">{{ dayName }}</div>
            <div v-for="day in calendarDays" :key="day.key" role="button" tabindex="0" :aria-pressed="String(day.highlighted)" :class="['month-day', { muted: !day.currentMonth, highlighted: day.highlighted }]" @click="selectCalendarDay(day)" @keydown.enter.prevent="selectCalendarDay(day)">
              <span class="day-number">{{ day.date.date() }}</span>
              <span class="month-events">
                <button v-for="booking in bookingsForDay(day)" :key="booking.id" type="button" class="month-event" @click.stop="selectMonthBooking(booking)">{{ booking.bookingName }}</button>
              </span>
              <article v-if="day.selected && selectedBooking && day.date.isSame(dayjsValue(selectedBooking.startDate), 'day')" class="month-detail" @click.stop>
                <button type="button" aria-label="Close booking detail" @click="selectedBooking = null">×</button>
                <strong>{{ selectedBooking.bookingName }}</strong>
                <small><v-icon size="15" color="#00378e">mdi-timer</v-icon>{{ formatTimeRange(selectedBooking) }}</small>
                <strong>{{ selectedRoom ? selectedRoom.name : "" }}</strong>
                <small>{{ selectedRoom ? localizedLocation(selectedRoom) : "" }}</small>
                <img src="@/assets/account-circle.png" alt="" />
              </article>
            </div>
          </div>
        </section>

        <aside class="information-panel">
          <h2>Booking Information</h2>
          <label>Zone<input :value="selectedRoom ? localizedLocation(selectedRoom) : ''" readonly /></label>
          <label>Room<input :value="selectedRoom ? selectedRoom.name : ''" readonly /></label>
          <label>Date<div class="input-icon"><input v-model="bookingForm.date" type="date" @change="onBookingDateChange" /><v-icon size="18" color="#00378e">mdi-calendar-check</v-icon></div></label>
          <div class="time-fields"><label>Start<input ref="startTimePicker" v-model="bookingForm.startTime" type="time" @click="openTimePicker('startTimePicker')" /></label><label>End<input ref="endTimePicker" v-model="bookingForm.endTime" type="time" @click="openTimePicker('endTimePicker')" /></label></div>
          <label>Meeting Name<input v-model.trim="bookingForm.meetingName" type="text" maxlength="200"  @keydown.enter="createBooking" /></label>
          <button class="create-booking" type="button" :disabled="creatingBooking" @click="createBooking"><span>+</span> {{ creatingBooking ? "Creating..." : "Create Booking" }}</button>
        </aside>
      </div>
    </section>

    <v-dialog v-model="loading" persistent width="auto">
      <v-card class="pa-6 d-flex flex-column align-center justify-center" elevation="8" rounded="xl">
        <v-progress-circular indeterminate color="indigo" size="70" width="6" />
        <span class="mt-4 text-subtitle-1 font-weight-medium">Loading, please wait...</span>
      </v-card>
    </v-dialog>
  </main>
</template>

<script>
import axios from "axios";
import dayjs from "dayjs";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";

export default {
  name: "ViewSchedule",
  data() {
    return {
      url: enurl.apiUrl,
      username: this.$cookies.get("Username") || "User",
      loading: false,
      errorMessage: "",
      updatedAt: null,
      selectedDate: dayjs(),
      highlightedDate: dayjs().format("YYYY-MM-DD"),
      viewMode: "D",
      selectedFacilityID: Number(this.$route.query.facilityID) || null,
      selectedZoneID: null,
      selectedFloorID: null,
      zones: [],
      rooms: [],
      floors: [],
      bookings: [],
      selectedBooking: null,
      creatingBooking: false,
      scheduleRequestID: 0,
      bookingForm: {
        date: dayjs().format("YYYY-MM-DD"),
        startTime: "09:00",
        endTime: "10:00",
        meetingName: "",
      },
      hours: Array.from({ length: 24 }, (_, index) => index),
      weekDays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    };
  },
  computed: {
    zoneOptions() {
      return this.floors.map((zone) => ({ value: zone.locationID, text: this.localizedLocation(zone) }));
    },
    floorOptions() {
      return this.zones
        .filter((floor) => this.selectedZoneID === null || floor.locationID === this.selectedZoneID)
        .map((floor) => ({ value: floor.zoneID, text: this.localizedZone(floor) }));
    },
    filteredRooms() {
      return this.rooms.filter((room) =>
        (this.selectedZoneID === null || room.locationID === this.selectedZoneID) &&
        (this.selectedFloorID === null || room.zoneID === this.selectedFloorID)
      );
    },
    selectedRoom() {
      return this.rooms.find((room) => room.facilityID === this.selectedFacilityID) || null;
    },
    visibleBookings() {
      return this.bookings;
    },
    bookingsByHour() {
      const groupedBookings = this.hours.reduce((result, hour) => {
        result[hour] = [];
        return result;
      }, {});

      this.visibleBookings
        .slice()
        .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf())
        .forEach((booking) => {
          const startHour = dayjs(booking.startDate).hour();
          groupedBookings[startHour].push(booking);
        });

      return groupedBookings;
    },
    calendarDays() {
      const monthStart = this.selectedDate.startOf("month");
      const gridStart = monthStart.subtract(monthStart.day(), "day");
      const monthEnd = monthStart.endOf("month");
      const cellCount = Math.ceil((monthEnd.diff(gridStart, "day") + 1) / 7) * 7;
      return Array.from({ length: cellCount }, (_, index) => {
        const date = gridStart.add(index, "day");
        return {
          key: date.format("YYYY-MM-DD"),
          date,
          currentMonth: date.month() === monthStart.month(),
          highlighted: date.format("YYYY-MM-DD") === this.highlightedDate,
          selected: date.isSame(this.selectedDate, "day"),
        };
      });
    },
  },
  mounted() { this.loadPage(); },
  beforeDestroy() { this.scheduleRequestID += 1; },
  methods: {
    async loadPage() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await axios.get(`${this.url}Dashboard/getDataDashboard`);
        if (response.data.status !== 0) throw new Error(response.data.message);
        const data = response.data.data || {};
        this.zones = data.zones || [];
        this.rooms = data.rooms || [];
        this.floors = data.floors || [];
        if (!this.selectedFacilityID || !this.rooms.some((room) => room.facilityID === this.selectedFacilityID)) this.selectedFacilityID = this.rooms[0]?.facilityID || null;
        const room = this.selectedRoom;
        this.selectedZoneID = room?.locationID ?? null;
        this.selectedFloorID = room?.zoneID ?? this.zones[0]?.zoneID ?? null;
        await this.loadSchedule();
      } catch (error) {
        this.errorMessage = error.response?.data?.message || error.message || "Unable to load schedule";
      } finally { this.loading = false; }
    },
    async loadSchedule() {
      const requestID = ++this.scheduleRequestID;
      this.errorMessage = "";
      if (!this.selectedFacilityID) {
        this.bookings = [];
        this.selectedBooking = null;
        this.updatedAt = null;
        this.loading = false;
        return;
      }
      this.loading = true;
      this.selectedBooking = null;
      try {
        const isMonth = this.viewMode === "M";
        const endpoint = isMonth ? "getDataRoomScheduleRange" : "getDataRoomSchedule";
        const params = isMonth
          ? { facilityID: this.selectedFacilityID, startDate: this.selectedDate.startOf("month").format("YYYY-MM-DD"), endDate: this.selectedDate.add(1, "month").startOf("month").format("YYYY-MM-DD") }
          : { facilityID: this.selectedFacilityID, date: this.selectedDate.format("YYYY-MM-DD") };
        const response = await axios.get(`${this.url}Dashboard/${endpoint}`, { params });
        if (requestID !== this.scheduleRequestID) return;
        if (response.data.status !== 0) throw new Error(response.data.message);
        const rawBookings = isMonth ? response.data.data : response.data.data?.bookings;
        this.bookings = (rawBookings || []).map((item) => ({ ...item, id: item.id || item.ID }));
        this.errorMessage = "";
        this.updatedAt = new Date();
      } catch (error) {
        if (requestID !== this.scheduleRequestID) return;
        this.bookings = [];
        this.selectedBooking = null;
        this.errorMessage = error.response?.data?.message || error.message || "Unable to load schedule";
      } finally { if (requestID === this.scheduleRequestID) this.loading = false; }
    },
    selectRoom(room) { this.selectedFacilityID = room.facilityID; this.loadSchedule(); },
    onZoneChange() { const firstFloor = this.floorOptions[0]; this.selectedFloorID = firstFloor?.value ?? null; const room = this.filteredRooms[0]; this.selectedFacilityID = room?.facilityID || null; this.loadSchedule(); },
    onFloorChange() { const room = this.filteredRooms[0]; this.selectedFacilityID = room?.facilityID || null; this.loadSchedule(); },
    movePeriod(step) {
      this.selectedDate = this.selectedDate.add(step, this.viewMode === "D" ? "day" : "month");
      if (this.viewMode === "D") this.highlightedDate = this.selectedDate.format("YYYY-MM-DD");
      this.resetBookingForm(this.selectedDate);
      this.loadSchedule();
    },
    setViewMode(mode) { if (this.viewMode === mode) return; this.viewMode = mode; this.loadSchedule(); },
    selectCalendarDay(day) {
      const periodChanged = !day.date.isSame(this.selectedDate, this.viewMode === "M" ? "month" : "day");
      this.selectedDate = day.date;
      this.highlightedDate = day.date.format("YYYY-MM-DD");
      this.resetBookingForm(day.date);
      if (periodChanged) {
        this.bookings = [];
        this.updatedAt = null;
        this.loadSchedule();
      }
    },
    resetBookingForm(date) {
      this.selectedBooking = null;
      this.bookingForm = {
        date: dayjs(date).format("YYYY-MM-DD"),
        startTime: "09:00",
        endTime: "10:00",
        meetingName: "",
      };
    },
    onBookingDateChange() {
      const selectedDate = dayjs(this.bookingForm.date);
      if (!selectedDate.isValid()) return;
      this.selectedDate = selectedDate;
      this.highlightedDate = selectedDate.format("YYYY-MM-DD");
      this.resetBookingForm(selectedDate);
      this.loadSchedule();
    },
    selectBooking(booking) {
      this.selectedBooking = booking;
      this.bookingForm = {
        date: dayjs(booking.startDate).format("YYYY-MM-DD"),
        startTime: dayjs(booking.startDate).format("HH:mm"),
        endTime: dayjs(booking.expireDate).format("HH:mm"),
        meetingName: booking.bookingName || "",
      };
    },
    selectMonthBooking(booking) {
      this.selectedDate = dayjs(booking.startDate);
      this.highlightedDate = this.selectedDate.format("YYYY-MM-DD");
      this.selectBooking(booking);
    },
    bookingsForDay(day) { return this.bookings.filter((booking) => dayjs(booking.startDate).isSame(day.date, "day")); },
    openTimePicker(refName) {
      const input = this.$refs[refName];
      if (!input) return;
      input.focus();
      if (typeof input.showPicker === "function") {
        try { input.showPicker(); } catch (error) { /* Browser keeps the focused native input as fallback. */ }
      }
    },
    async createBooking() {
      if (this.creatingBooking) return;
      const { date, startTime, endTime, meetingName } = this.bookingForm;
      if (!this.selectedFacilityID || !date || !startTime || !endTime || !meetingName) {
        await Swal.fire({ icon: "warning", width: 700, text: "กรุณากรอก Date, Start, End และ Meeting Name ให้ครบ" });
        return;
      }
      const start = dayjs(`${date}T${startTime}`);
      const end = dayjs(`${date}T${endTime}`);
      if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
        await Swal.fire({ icon: "warning", width: 700, text: "เวลาสิ้นสุดต้องมากกว่าเวลาเริ่มต้น" });
        return;
      }

      this.creatingBooking = true;
      this.loading = true;
      try {
        const response = await axios.post(`${this.url}Calendar/createMeetingOffice365`, {
          title: meetingName,
          organizer: this.username,
          facilityID: this.selectedFacilityID,
          startDate: date,
          startTime,
          endDate: date,
          endTime,
        });
        if (response.data.status !== 0) throw new Error(response.data.message || "Create booking failed");
        this.selectedDate = dayjs(date);
        this.highlightedDate = this.selectedDate.format("YYYY-MM-DD");
        this.bookingForm.meetingName = "";
        await this.loadSchedule();
        await Swal.fire({ icon: "success", width: 700, text: "สร้าง Meeting สำเร็จ", timer: 1600, showConfirmButton: false });
      } catch (error) {
        await Swal.fire({ icon: "warning", width: 700, text: error.response?.data?.message || error.message || "ไม่สามารถสร้าง Meeting ได้" });
      } finally {
        this.loading = false;
        this.creatingBooking = false;
      }
    },
    toggleLanguage() { this.$i18n.locale = this.$i18n.locale === "en" ? "th" : "en"; },
    Logout() { this.$store.dispatch("Logout").finally(() => this.$router.push("/Login").catch(() => {})); },
    localizedZone(item) { return this.$i18n.locale === "th" ? (item.zoneNameTH || item.zoneName) : (item.zoneName || item.zoneNameTH); },
    localizedLocation(item) { return this.$i18n.locale === "th" ? (item.locationNameTH || item.locationName) : (item.locationName || item.locationNameTH); },
    formatDate(value) { return dayjs(value).format("DD MMMM YYYY"); },
    formatMonth(value) { return dayjs(value).format("MMMM YYYY"); },
    dayjsValue(value) { return dayjs(value); },
    formatClock(value) { return value ? dayjs(value).format("HH:mm:ss") : "—"; },
    formatTime(value) { return dayjs(value).format("hh.mm a"); },
    formatTimeRange(item) { return `${this.formatTime(item.startDate)} - ${this.formatTime(item.expireDate)}`; },
    hourLabel(hour) { const suffix = hour < 12 ? "AM" : "PM"; const display = hour % 12 || 12; return `${display} ${suffix}`; },
    isCompactBooking(booking) { return dayjs(booking.expireDate).diff(dayjs(booking.startDate), "minute") < 60; },
  },
};
</script>

<style scoped>
.schedule-page{min-height:100vh;padding:28px 46px 48px;background:#f6f6f6;color:#111}.schedule-topbar{display:flex;align-items:center;height:81px;padding:0 10px;border-radius:50px;background:#fff}.brand{display:grid;width:65px;height:65px;place-items:center;border:0;border-radius:50%;background:#edf1f5}.brand img{width:42px}.schedule-topbar>div:nth-child(2){margin-left:24px}.schedule-topbar h1{margin:0;font-size:30px}.schedule-topbar p{margin:2px 0 0;font-size:13px}.account-actions{display:flex;align-items:center;gap:10px;margin-left:auto}.language{width:80px;height:55px;border:0;border-radius:40px;background:#00368e;color:#fff;font-size:18px}.profile{display:flex;align-items:center;width:214px;height:55px;padding:4px 8px;border:0;border-radius:40px;background:#fff;box-shadow:0 4px 4px rgba(0,0,0,.25);text-align:left}.profile img{width:49px;height:46px}.profile span{display:flex;min-width:0;flex:1;flex-direction:column;margin-left:10px;color:#00378e}.profile strong,.profile small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.profile small{font-size:10px}.booking-shell{margin-top:28px;border-radius:15px;background:#ebeae9;box-shadow:0 6px 12px rgba(0,0,0,.2)}.booking-title{display:flex;align-items:center;justify-content:center;height:63px;border-radius:15px 15px 0 0;background:#00378e;color:#fff}.booking-title button{position:absolute;right:64px;border:0;background:transparent}.booking-layout{display:grid;grid-template-columns:323px minmax(500px,1fr) 361px;gap:12px;min-height:760px}.facility-panel,.timeline-panel,.information-panel{background:#fff;box-shadow:0 6px 12px rgba(0,0,0,.14)}.facility-panel{padding:24px;border-radius:0 0 15px 15px}.facility-panel h2,.timeline-panel h2,.information-panel h2{margin:0 0 16px;color:#00378e;font-size:20px}.facility-panel label,.information-panel label{display:flex;flex-direction:column;gap:6px;margin-bottom:10px;font-size:12px}.facility-panel::v-deep .v-input__slot{min-height:42px!important;border-radius:24px!important}.floor-input{width:110px;margin-top:12px}.room-heading{display:flex;align-items:center;justify-content:space-between;margin:20px 0 8px;color:#00378e}.room-heading h3{margin:0;font-size:20px}.room-heading small{color:#828282}.room-option{display:flex;width:100%;align-items:center;gap:12px;padding:12px 8px;border:0;background:transparent;text-align:left}.room-option>i{width:12px;height:12px;border:1px solid #75859b;border-radius:50%}.room-option.active>i{border-color:#00378e;background:#00378e}.room-option span{display:flex;flex-direction:column}.room-option small{display:flex;align-items:center;color:#00378e}.timeline-panel{border-radius:0 0 15px 15px;overflow:hidden}.timeline-heading{position:relative;height:138px;padding:22px}.date-nav{display:flex;align-items:center;justify-content:center;gap:8px;color:#00378e;font-size:20px}.date-nav button{border:0;background:transparent}.view-toggle{position:absolute;right:18px;bottom:18px;display:flex}.view-toggle button{width:40px;height:32px;border:0;background:#ebf4ff;color:#00378e}.view-toggle button:first-child{border-radius:17px 0 0 17px}.view-toggle button:last-child{border-radius:0 17px 17px 0}.view-toggle button.active{background:#bacde7}.timeline-scroll{height:620px;overflow-y:auto}.timeline-grid{position:relative;min-height:845px;margin-left:50px}.hour-row{display:grid;grid-template-columns:50px 1fr;height:65px}.hour-row time{padding:5px 8px 0 0;color:#828282;font-size:11px;text-align:right}.hour-row span{border-top:1px solid #ddd}.booking-block{position:absolute;right:31px;left:52px;display:flex;flex-direction:column;align-items:flex-start;gap:1px;padding:6px 26px;border:2px solid transparent;border-radius:10px;background:#00368e;color:#fff;text-align:left;overflow:hidden}.booking-block.selected{border-color:#72a8ff}.booking-block strong{max-width:100%;font-size:16px;line-height:19px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.booking-block small{font-size:12px;line-height:14px}.booking-block .booking-host{display:flex;align-items:center;margin-top:3px}.booking-block img{width:24px;height:24px;border-radius:50%}.booking-block.compact{gap:0;padding:4px 18px}.booking-block.compact strong{font-size:14px;line-height:16px}.booking-block.compact small{font-size:10px;line-height:12px}.booking-block.compact .booking-host{margin-top:1px}.booking-block.compact img{width:18px;height:18px}.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}.timeline-empty{position:absolute;top:90px;right:0;left:50px;color:#828282;text-align:center}.information-panel{position:relative;padding:24px 22px;border-radius:0 0 15px 15px}.information-panel input{width:100%;height:48px;padding:0 16px;border:1px solid rgba(194,198,214,.45);border-radius:24px;color:#4c4c4c;outline:0}.information-panel label:nth-of-type(-n+2) input{background:#ebeae9}.input-icon{position:relative}.input-icon .v-icon{position:absolute;top:15px;right:14px}.time-fields{display:grid;grid-template-columns:1fr 1fr;gap:20px}.create-booking{position:absolute;right:22px;bottom:20px;left:22px;height:45px;border:0;border-radius:24px;background:#00368e;box-shadow:0 4px 4px rgba(0,0,0,.25);color:#fff;font-size:16px}.create-booking span{margin-right:14px}.schedule-error{display:flex;align-items:center;gap:10px;padding:10px 18px;background:#ffebe8;color:#7a271a}.schedule-error span{flex:1}.schedule-error button{border:0;background:transparent;color:#00378e}.schedule-loading{height:4px}.empty-copy{padding:30px;color:#828282;text-align:center}
.month-calendar{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));grid-template-rows:42px;grid-auto-rows:minmax(0,1fr);height:693px;overflow:visible;border-top:1px solid #ddd}.weekday{display:flex;align-items:center;justify-content:center;color:#00368e;font-size:16px;font-weight:500}.month-day{position:relative;min-width:0;min-height:0;padding:13px 5px;border-top:1px solid #ddd;border-right:1px solid #ddd;background:#fff;color:#00368e;cursor:pointer}.month-day:nth-child(7n+1){border-left:1px solid #ddd}.month-day.muted{background:#fafafa;color:#8795aa}.month-day.highlighted{background:#e7effc}.day-number{display:block;font-size:12px;text-align:center}.month-events{display:flex;max-height:calc(100% - 28px);flex-direction:column;gap:2px;margin-top:8px;overflow-y:auto}.month-event{display:block;width:100%;height:21px;min-height:21px;overflow:hidden;padding:2px 7px;border:0;border-radius:5px;background:#00368e;color:#fff;font-size:11px;text-align:left;text-overflow:ellipsis;white-space:nowrap}.month-event:focus-visible{outline:2px solid #72a8ff;outline-offset:1px}.month-detail{position:absolute;z-index:4;top:48px;left:calc(100% + 5px);display:flex;width:200px;height:170px;flex-direction:column;align-items:flex-start;padding:24px 22px;border-radius:10px;background:#ddd;box-shadow:0 4px 8px rgba(0,0,0,.16);color:#111;text-align:left}.month-detail>button{position:absolute;top:7px;right:9px;border:0;background:transparent;color:#00368e}.month-detail strong{font-size:13px}.month-detail small{display:flex;align-items:center;color:#00368e;font-size:10px}.month-detail img{width:23px;height:23px;margin-top:auto;border-radius:50%}
.timeline-grid{min-height:1040px}
.hour-row{height:80px}
.information-panel input{border-color:#e4e7ef;background:#fff;color:#4c4c4c;-webkit-text-fill-color:#4c4c4c;color-scheme:light;opacity:1}
.information-panel input[readonly]{border-color:#d9d9de;background:#ebeae9}
.information-panel .time-fields input[type="time"]{border-color:#e4e7ef;background:#fff!important}
.information-panel input::placeholder{color:#828282;opacity:1}
.information-panel h2{margin-bottom:22px;font-family:Inter,sans-serif;font-size:20px;font-weight:600;line-height:24px}
.information-panel label{gap:9px;margin-bottom:17px;color:#333;font-family:Inter,sans-serif;font-size:14px;font-weight:400;line-height:18px}
.information-panel input{height:48px;padding:0 16px;border-width:1px;border-style:solid;border-radius:24px;font-family:Inter,sans-serif;font-size:16px;font-weight:400;line-height:19px}
.information-panel .input-icon input{padding-right:48px}
.information-panel .time-fields{grid-template-columns:repeat(2,minmax(0,1fr));gap:20px}
.information-panel .time-fields label{min-width:0}
.information-panel .time-fields input{min-width:0}
/* Figma frame 519:4561 and 532:4945 — exact desktop geometry at 1920 × 1080. */
.schedule-page,.schedule-page *{box-sizing:border-box}.schedule-page{min-height:1080px;padding:28px 0 48px}.schedule-topbar{width:min(1836px,calc(100vw - 92px));height:81px;padding:0 9px 0 11px;margin:0 auto}.brand{flex:0 0 65px}.schedule-topbar>div:nth-child(2){margin-left:23px}.schedule-topbar h1{line-height:39px}.schedule-topbar p{margin:0 0 0 6px}.profile{padding:4px 8px 4px 5px}.booking-shell{width:min(1814px,calc(100vw - 110px));height:895px;margin:28px auto 0}.booking-title{position:relative;width:100%;height:63px}.booking-title button{top:17px;right:15px;width:30px;height:30px;padding:0}.booking-layout{grid-template-columns:323fr 1108fr 361fr;gap:11px;width:100%;height:832px;min-height:832px}.facility-panel,.timeline-panel,.information-panel{min-width:0;height:831px}.facility-panel{padding:26px 24px}.timeline-heading{height:138px;padding:22px 39px}.timeline-scroll{height:693px}.information-panel{padding:26px 21px}.information-panel input{width:100%;max-width:100%}.create-booking{right:24px;bottom:34px;left:24px}
@media(min-width:960px){
  .schedule-page{padding-top:18px}
  .schedule-topbar{height:68px}
  .brand{width:54px;height:54px;flex-basis:54px}
  .brand img{width:36px}
  .schedule-topbar>div:nth-child(2){margin-left:18px}
  .schedule-topbar h1{font-size:24px;line-height:30px}
  .schedule-topbar p{font-size:11px}
  .language{width:68px;height:46px;font-size:16px}
  .profile{width:184px;height:46px}
  .profile img{width:40px;height:38px}
  .booking-shell{margin-top:20px}
}
@media(max-width:1366px){
  .schedule-page{min-height:100vh;padding:18px}
  .schedule-topbar,.booking-shell{width:100%}
  .booking-shell{height:auto}
  .booking-layout{grid-template-columns:220px minmax(0,1fr);height:auto;min-height:720px}
  .facility-panel,.timeline-panel{height:720px}
  .information-panel{grid-column:1/-1;min-height:460px;height:460px}
  .booking-title button{right:15px}
  .timeline-scroll{height:582px}
  .room-option{min-height:48px}
  .date-nav button,.view-toggle button{min-width:44px;min-height:44px}
}
@media(max-width:959px){
  .schedule-page{padding:10px}
  .schedule-topbar h1{font-size:20px}
  .schedule-topbar p,.profile span,.profile>.v-icon{display:none}
  .profile{width:55px}
  .language{width:55px}
  .booking-layout{display:block;min-height:0}
  .facility-panel,.timeline-panel,.information-panel{width:100%;height:auto;margin-bottom:10px}
  .facility-panel{max-height:420px;overflow-y:auto}
  .timeline-panel{min-height:720px}
  .information-panel{min-height:500px}
  .timeline-heading h2{font-size:16px}
  .date-nav{font-size:16px}
  .timeline-scroll{height:582px}
  .month-calendar{height:620px;overflow:hidden}
  .booking-block{right:8px;left:48px;padding:10px}
  .booking-title button{right:20px}
}
@media(min-width:960px) and (max-width:1366px) and (orientation:landscape){
  .booking-shell{overflow:hidden}
  .booking-title{height:52px;font-size:15px}
  .booking-title button{top:11px;width:30px;height:30px}
  .booking-layout{grid-template-columns:minmax(170px,22%) minmax(0,1fr) minmax(230px,28%);height:560px;min-height:560px;gap:8px}
  .facility-panel,.timeline-panel,.information-panel{height:560px;min-height:0}
  .facility-panel{padding:16px 14px;overflow-y:auto}
  .facility-panel h2,.timeline-panel h2,.information-panel h2{margin-bottom:10px;font-size:16px}
  .facility-panel label,.information-panel label{margin-bottom:7px;font-size:11px}
  .facility-panel::v-deep .v-input__slot{min-height:38px!important}
  .floor-input{width:100%;margin-top:8px}
  .room-heading{margin:14px 0 5px}
  .room-heading h3{font-size:16px}
  .room-option{min-height:42px;padding:7px 5px;gap:8px}
  .timeline-heading{height:100px;padding:12px 16px}
  .date-nav{font-size:16px}
  .view-toggle{right:12px;bottom:8px}
  .timeline-scroll{height:460px}
  .month-calendar{height:460px;overflow:hidden}
  .weekday{font-size:13px}
  .month-day{padding:7px 3px}
  .month-events{margin-top:4px}
  .information-panel{grid-column:auto;padding:16px 14px}
  .information-panel input{height:40px;padding:0 12px;font-size:12px}
  .input-icon .v-icon{top:11px;right:10px}
  .time-fields{gap:8px}
  .create-booking{right:14px;bottom:14px;left:14px;height:42px;font-size:14px}
}

.timeline-grid {
  min-height: 1040px;
}

.hour-row {
  height: auto;
  min-height: 80px;
}

.hour-slot {
  display: flex;
  min-width: 0;
  min-height: 80px;
  flex-direction: column;
  gap: 4px;
  padding: 4px 31px 4px 2px;
  border-top: 1px solid #ddd;
}

.booking-block {
  position: relative;
  right: auto;
  left: auto;
  width: 100%;
  min-height: 76px;
  flex: none;
}

.booking-block.compact {
  min-height: 58px;
}
</style>
